import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { AsideSection } from "@/components/AsideSection";
import AddressLookup from "@/components/AddressLookup";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, X, Info, Users, MapPin, MessageCircle, ChevronDown, Plus } from "lucide-react";
import { useEnquirySources } from "@/components/EnquirySource";
import { useAuth } from "@/context/AuthContext";
import { NewCustomerInline } from "@/components/NewCustomerInline";

const selCls = "block w-full appearance-none rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 pr-9 text-sm text-slate-900 transition-[border-color,box-shadow] focus:border-apple-blue focus:ring-4 focus:ring-apple-blue/15 focus:outline-none";
const textareaCls = "mt-1 w-full px-3 py-2 text-sm rounded-lg bg-slate-50 border border-slate-200 text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400";

function Lbl({ children, badge }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[13px] font-medium text-slate-900">{children}</span>
      {badge && <span className="text-[10px] font-semibold uppercase tracking-wide text-indigo-500 bg-indigo-50 rounded-full px-2 py-0.5">{badge}</span>}
    </div>
  );
}

function Sel({ value, onChange, children, testid, placeholder = "Please select" }) {
  return (
    <div className="relative mt-1">
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className={selCls}>
        <option value="">{placeholder}</option>
        {children}
      </select>
      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
    </div>
  );
}

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINS = ["00", "15", "30", "45"];

export default function JobForm({ mode = "create", task, onClose, onSaved }) {
  const navigate = useNavigate();
  const isEdit = mode === "edit";
  const { user } = useAuth();
  const [opts, setOpts] = useState({ job_types: [], work_types: [], depots: ["HQ"], staff: [], sales_staff: [], equipment: [] });
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    title: task?.title || "",
    lead_source: task?.lead_source || "",
    enquiry_source_id: task?.enquiry_source_id || "",
    task_type: task?.task_type || "",
    work_type: task?.work_type || "",
    depot: task?.depot || "",
    client_instructions: task?.client_instructions || "",
    work_instructions: task?.work_instructions || "",
    public_contact: task?.public_contact || "",
    customer_legacy_id: task?.customer_legacy_id || "",
    po_number: task?.po_number || "",
    site_name: task?.site_name || "",
    sales_contact: task?.sales_contact || "",
    equipment: task?.equipment || "",
    contributors: task?.contributors || [],
    est_install_from: task?.est_install_from || "",
    est_install_to: task?.est_install_to || "",
    install_date: task?.install_date || "",
    install_hour: (task?.install_time || "").split(":")[0] || "",
    install_min: (task?.install_time || "").split(":")[1] || "",
    notify_customer: !!task?.notify_customer,
    reset_install_approval: !!task?.reset_install_approval,
    schedule_shift: !!task?.schedule_shift,
    recurring_shift: !!task?.recurring_shift,
    workbank_shift: !!task?.workbank_shift,
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const [custResults, setCustResults] = useState([]);
  const [custOpen, setCustOpen] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [newCust, setNewCust] = useState(false);
  const [addrMode, setAddrMode] = useState("new");
  const [addrIdx, setAddrIdx] = useState(0);
  const [newAddr, setNewAddr] = useState({ label: "", line1: task?.full_address || "", line2: "", city: task?.venue_city || "", postcode: task?.venue_postcode || "", country: "UK" });
  const justPicked = useRef(false);
  const fmtAddr = (a) => [a.label, a.line1, a.city, a.postcode].filter(Boolean).join(", ") || "Untitled address";
  const onLookup = (a) => { setAddrMode("new"); setNewAddr({ label: "", line1: a.address_line_1 || "", line2: a.address_line_2 || a.county || "", city: a.town_city || "", postcode: a.postcode || "", country: a.country || "United Kingdom" }); };

  useEffect(() => {
    const q = (f.public_contact || "").trim();
    if (justPicked.current) { justPicked.current = false; return; }
    if (q.length < 2) { setCustResults([]); setCustOpen(false); return; }
    const t = setTimeout(async () => {
      try { const r = await api.get(`/customers/search`, { params: { q } }); setCustResults(r.data.items || []); setCustOpen(true); } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [f.public_contact]);

  const pickCustomer = (c) => {
    justPicked.current = true;
    setCustomer(c);
    setCustResults([]); setCustOpen(false);
    setF((s) => ({ ...s, public_contact: c.full_name || "", customer_legacy_id: c.legacy_id }));
    if (c.addresses?.length) { setAddrMode("existing"); setAddrIdx(0); }
    else { setAddrMode("new"); }
  };

  useEffect(() => {
    api.get("/tasks/meta/form-options").then((r) => {
      setOpts({
        job_types: r.data.job_types || [], work_types: r.data.work_types || [],
        depots: r.data.depots?.length ? r.data.depots : ["HQ"],
        staff: r.data.staff || [], sales_staff: r.data.sales_staff || [], equipment: r.data.equipment || [],
      });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit && !f.sales_contact && user?.name && opts.sales_staff.includes(user.name)) setF((s) => ({ ...s, sales_contact: user.name }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.sales_staff, user]);

  useEffect(() => {
    if (!f.depot && opts.depots.length === 1) setF((s) => ({ ...s, depot: opts.depots[0] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.depots]);

  const [enquirySources] = useEnquirySources(true);
  const uniq = (base, cur) => Array.from(new Set([...(base || []), cur].filter(Boolean)));

  const save = async () => {
    if (!f.title.trim()) { toast.error("Title is required"); return; }
    if (!task && ["", "enquiry"].includes((f.task_type || "").toLowerCase()) && enquirySources.some((s) => s.active) && !f.enquiry_source_id) {
      toast.error("Please choose an Enquiry Source"); return;
    }
    setBusy(true);
    const install_time = f.install_hour ? `${f.install_hour}:${f.install_min || "00"}` : "";
    const addr = (customer?.addresses?.length && addrMode === "existing") ? customer.addresses[addrIdx] : newAddr;
    const hasAddr = addr && (addr.line1 || addr.city || addr.postcode);
    const payload = {
      title: f.title, task_type: f.task_type, work_type: f.work_type, depot: f.depot || null,
      lead_source: f.lead_source || null,
      enquiry_source_id: f.enquiry_source_id || null,
      client_instructions: f.client_instructions, work_instructions: f.work_instructions,
      public_contact: f.public_contact || null, po_number: f.po_number || null,
      customer_legacy_id: f.customer_legacy_id || null,
      sales_contact: f.sales_contact || (isEdit ? "" : null),
      equipment: f.equipment || null, contributors: f.contributors,
      est_install_from: f.est_install_from || null, est_install_to: f.est_install_to || null,
      install_date: f.install_date || null, install_time: install_time || null,
      notify_customer: f.notify_customer, reset_install_approval: f.reset_install_approval,
      schedule_shift: f.schedule_shift, recurring_shift: f.recurring_shift, workbank_shift: f.workbank_shift,
    };
    if (hasAddr) {
      payload.site_address = addr;
      payload.site_name = addr.label || addr.line1 || null;
      payload.venue_name = addr.label || addr.line1 || null;
      payload.venue_city = addr.city || null;
      payload.venue_postcode = addr.postcode || null;
      payload.full_address = [addr.line1, addr.line2, addr.city, addr.postcode].filter(Boolean).join(", ") || null;
    }
    try {
      if (isEdit) {
        await api.patch(`/tasks/${task.legacy_id}`, payload);
        toast.success("Job updated (demo database)");
        onSaved?.();
      } else {
        const r = await api.post(`/tasks`, payload);
        toast.success("Job created");
        onSaved?.();
        navigate(`/tasks/${r.data.legacy_id}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save job");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/25 backdrop-blur-sm flex justify-end" onClick={onClose} data-testid="job-form-modal">
      <div className="relative bg-white h-full w-full sm:w-1/2 sm:max-w-none shadow-panel overflow-hidden flex flex-col sm:rounded-l-[24px] animate-in slide-in-from-right duration-300" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} aria-label="Close" className="absolute right-5 top-5 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"><X className="w-5 h-5" /></button>
        <div className="flex flex-col px-8 pt-8 pb-5 pr-20">
          <h3 className="text-[28px] font-bold tracking-tight text-slate-900" data-testid="job-form-title">{isEdit ? "Edit Job" : "New Job"}</h3>
          <p className="ted-subtle mt-1">{isEdit ? "Update the job details below." : "Add the details for the new job. Required fields are marked."}</p>
        </div>

        <div className="flex-1 overflow-y-auto px-8">
          <AsideSection first icon={Info} title="Job Information" description="Basic information about the job.">
            <div><Lbl>Enquiry Source{!task && ["", "enquiry"].includes((f.task_type || "").toLowerCase()) ? <span className="text-rose-500"> *</span> : null}</Lbl>
              <Sel value={f.enquiry_source_id} onChange={(v) => set("enquiry_source_id", v)} testid="job-enquiry-source">
                {enquirySources.filter((s) => s.active || s.id === f.enquiry_source_id).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </Sel></div>
            <div><Lbl>Title</Lbl>
              <Input value={f.title} onChange={(e) => set("title", e.target.value)} className="mt-1" data-testid="job-title" /></div>
            <div><Lbl>Job Type</Lbl>
              <Sel value={f.task_type} onChange={(v) => set("task_type", v)} testid="job-type">
                {uniq(opts.job_types, f.task_type).map((d) => <option key={d} value={d}>{d}</option>)}
              </Sel></div>
            <div><Lbl>Work Type</Lbl>
              <Sel value={f.work_type} onChange={(v) => set("work_type", v)} testid="job-work-type">
                {uniq(opts.work_types, f.work_type).map((d) => <option key={d} value={d}>{d}</option>)}
              </Sel></div>
            <div><Lbl>Client Instructions</Lbl>
              <textarea rows={3} value={f.client_instructions} onChange={(e) => set("client_instructions", e.target.value)} className={textareaCls} data-testid="job-client-instructions" />
              <p className="text-[11px] text-slate-400 mt-1">The instruction the client has given you. This is not visible to site engineers.</p></div>
            <div><Lbl>Work Instructions</Lbl>
              <textarea rows={3} value={f.work_instructions} onChange={(e) => set("work_instructions", e.target.value)} className={textareaCls} data-testid="job-work-instructions" />
              <p className="text-[11px] text-slate-400 mt-1">This is the instruction to the engineers about what to accomplish onsite.</p></div>
            <div><Lbl>Estimated Installation Date</Lbl>
              <div className="mt-1 grid grid-cols-2 gap-2">
                <Input type="date" value={f.est_install_from} onChange={(e) => set("est_install_from", e.target.value)} data-testid="job-est-from" />
                <Input type="date" value={f.est_install_to} onChange={(e) => set("est_install_to", e.target.value)} data-testid="job-est-to" />
              </div></div>
            <div><Lbl>Installation Date</Lbl>
              <div className="mt-1 grid grid-cols-[1fr_auto_auto] gap-2 items-center">
                <Input type="date" value={f.install_date} onChange={(e) => set("install_date", e.target.value)} data-testid="job-install-date" />
                <Sel value={f.install_hour} onChange={(v) => set("install_hour", v)} testid="job-install-hour" placeholder="00">
                  {HOURS.map((h) => <option key={h} value={h}>{h}</option>)}
                </Sel>
                <Sel value={f.install_min} onChange={(v) => set("install_min", v)} testid="job-install-min" placeholder="00">
                  {MINS.map((m) => <option key={m} value={m}>{m}</option>)}
                </Sel>
              </div></div>
            <div className="flex items-center justify-between"><span className="text-sm text-slate-600">Notify Customer Of Installation Date</span>
              <Switch checked={f.notify_customer} onCheckedChange={(v) => set("notify_customer", v)} data-testid="job-notify" /></div>
            <div className="flex items-center justify-between"><span className="text-sm text-slate-600">Reset Installation Date Approval Status</span>
              <Switch checked={f.reset_install_approval} onCheckedChange={(v) => set("reset_install_approval", v)} data-testid="job-reset-approval" /></div>
          </AsideSection>

          <AsideSection icon={Users} title="Client Information" description="Search existing customers to auto-fill name and address.">
            <div className="relative">
              <Lbl badge="EXISTING">Public User</Lbl>
              <Input value={f.public_contact} autoComplete="off"
                onChange={(e) => { set("public_contact", e.target.value); setCustomer(null); setF((s) => ({ ...s, customer_legacy_id: "" })); }}
                onFocus={() => { if ((f.public_contact || "").trim().length >= 2 && !customer) setCustOpen(true); }}
                onBlur={() => setTimeout(() => setCustOpen(false), 150)}
                placeholder="Search existing customers by name or company" className="mt-1" data-testid="job-public-user" />
              {custOpen && !customer && (
                <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-72 overflow-auto" data-testid="job-customer-results">
                  {custResults.map((c) => (
                    <button key={c.legacy_id} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pickCustomer(c)}
                      className="w-full text-left px-3 py-2 hover:bg-indigo-50 border-b border-slate-50 last:border-0" data-testid={`job-customer-${c.legacy_id}`}>
                      <div className="text-sm font-medium text-slate-800">{c.full_name}{c.company ? ` · ${c.company}` : ""}</div>
                      <div className="text-xs text-slate-400">{c.addresses?.length ? `${c.addresses.length} saved address${c.addresses.length > 1 ? "es" : ""}` : "No saved address"}{c.addresses?.[0] ? ` · ${c.addresses[0].city || c.addresses[0].postcode || ""}` : ""}</div>
                    </button>
                  ))}
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { setNewCust(true); setCustOpen(false); }}
                    className="sticky bottom-0 w-full text-left px-3 py-2.5 bg-white border-t border-slate-100 hover:bg-indigo-50 text-sm font-semibold text-indigo-600 inline-flex items-center gap-1.5" data-testid="job-create-customer-option">
                    <Plus className="w-4 h-4" /> Create new customer{f.public_contact?.trim() ? ` "${f.public_contact.trim()}"` : ""}
                  </button>
                </div>
              )}
              {newCust && !customer && (
                <NewCustomerInline initialName={f.public_contact} onCancel={() => setNewCust(false)}
                  onCreated={(c) => { setNewCust(false); pickCustomer(c); }} />
              )}
              {customer && <div className="text-[11px] text-emerald-600 mt-1" data-testid="job-customer-selected">Linked to existing customer · {customer.addresses?.length || 0} saved address{(customer.addresses?.length || 0) === 1 ? "" : "es"}</div>}
            </div>
            <div><Lbl>PO Number</Lbl>
              <Input value={f.po_number} onChange={(e) => set("po_number", e.target.value)} className="mt-1" data-testid="job-po-number" /></div>
          </AsideSection>

          <AsideSection icon={MapPin} title="Site Information" description="Look up by postcode, choose a saved address, or add a new one.">
            <AddressLookup onSelect={onLookup} />
            {customer?.addresses?.length ? (
              <div><Lbl>Site Address</Lbl>
                <Sel value={addrMode === "new" ? "new" : String(addrIdx)} testid="job-address-select" placeholder="Select an address"
                  onChange={(v) => { if (v === "new") setAddrMode("new"); else { setAddrMode("existing"); setAddrIdx(Number(v)); } }}>
                  {customer.addresses.map((a, i) => <option key={i} value={String(i)}>{fmtAddr(a)}</option>)}
                  <option value="new">＋ Add a new address</option>
                </Sel>
              </div>
            ) : null}
            {(addrMode === "new" || !customer?.addresses?.length) && (
              <div className="space-y-2" data-testid="job-new-address">
                <Input value={newAddr.line1} onChange={(e) => setNewAddr((s) => ({ ...s, line1: e.target.value }))} placeholder="Address line 1" data-testid="job-addr-line1" />
                <Input value={newAddr.line2} onChange={(e) => setNewAddr((s) => ({ ...s, line2: e.target.value }))} placeholder="Address line 2 (optional)" data-testid="job-addr-line2" />
                <div className="grid grid-cols-2 gap-2">
                  <Input value={newAddr.city} onChange={(e) => setNewAddr((s) => ({ ...s, city: e.target.value }))} placeholder="City" data-testid="job-addr-city" />
                  <Input value={newAddr.postcode} onChange={(e) => setNewAddr((s) => ({ ...s, postcode: e.target.value }))} placeholder="Postcode" data-testid="job-addr-postcode" />
                </div>
              </div>
            )}
          </AsideSection>

          <AsideSection icon={MessageCircle} title="Further Information" description="Basic information about the job.">
            <div><Lbl>Sales Contact</Lbl>
              <Sel value={f.sales_contact} onChange={(v) => set("sales_contact", v)} testid="job-sales-contact">
                {uniq(opts.sales_staff, f.sales_contact).map((s) => <option key={s} value={s}>{s}</option>)}
              </Sel></div>
            <div className="flex items-center justify-between"><span className="text-sm text-slate-600">Schedule A Shift Now</span>
              <Switch checked={f.schedule_shift} onCheckedChange={(v) => set("schedule_shift", v)} data-testid="job-schedule-shift" /></div>
          </AsideSection>
        </div>

        <div className="px-8 py-4 border-t border-slate-200/70 flex justify-end gap-2 bg-white/90 backdrop-blur">
          <Button variant="secondary" className="h-10 px-5" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="h-10 px-6 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="job-save">
            {busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Save
          </Button>
        </div>
      </div>
    </div>
  );
}
